import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlignLeft, List as ListIcon, ListOrdered, X, Upload, Trash2, Star } from "lucide-react";
import { api, imgUrl, formatApiError } from "../../api";
import { STANDARD_PRODUCT_SIZES, normalizeProductSizes, sizeTestId } from "../../data/sizing";
import {
  assignmentContainsSubcategory,
  buildCategoryAssignment,
  categoryAssignmentKey,
  derivePrimaryCategoryFields,
  normalizeProductCategoryAssignments,
  subcategoryAssignmentKey,
} from "../../utils/productCategoryAssignments";
import { formatDescriptionLines } from "../../utils/productDescription";

const emptyProduct = {
  name: "",
  slug: "",
  description: "",
  category: "",
  category_id: null,
  category_slug: null,
  original_price: "",
  offer_price: "",
  discount_percentage: "",
  sizes: [...STANDARD_PRODUCT_SIZES],
  colors: [],
  images: [],
  main_image: "",
  delivery_time_days: "7",
  is_featured: false,
  is_new_arrival: false,
  is_bestseller: false,
  is_top_rated: false,
  is_old_deal: false,
  is_active: true,
  look_number: "",
  short_description: "",
  customization: "",
  subcategory: "",
  subcategory_id: null,
  subcategory_slug: null,
  category_assignments: [],
  tags: [],
};

export default function ProductForm({ product, categories, onClose, onSaved }) {
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [colorInput, setColorInput] = useState("");
  const fileRef = useRef(null);
  const descriptionRef = useRef(null);
  const categoryAssignments = normalizeProductCategoryAssignments(form, categories);

  useEffect(() => {
    if (product) {
      const placementFields = derivePrimaryCategoryFields(
        normalizeProductCategoryAssignments(product, categories),
        categories,
      );
      setForm({
        ...emptyProduct,
        ...product,
        ...placementFields,
        original_price: product.original_price ?? "",
        offer_price: product.offer_price ?? "",
        discount_percentage: product.discount_percentage ?? "",
        delivery_time_days: String(product.delivery_time_days ?? 7),
        colors: product.colors || [],
        images: product.images || [],
        sizes: normalizeProductSizes(product.sizes),
      });
    } else {
      setForm({ ...emptyProduct, sizes: [...STANDARD_PRODUCT_SIZES] });
    }
  }, [product, categories]);

  const updateCategoryAssignments = (updater) => {
    setForm((previous) => {
      const current = normalizeProductCategoryAssignments(previous, categories);
      const next = typeof updater === "function" ? updater(current) : updater;
      return {
        ...previous,
        ...derivePrimaryCategoryFields(next, categories),
      };
    });
  };

  const toggleCategory = (category) => {
    const wantedKey = categoryAssignmentKey(buildCategoryAssignment(category));
    updateCategoryAssignments((current) => {
      const exists = current.some((assignment) => categoryAssignmentKey(assignment) === wantedKey);
      return exists
        ? current.filter((assignment) => categoryAssignmentKey(assignment) !== wantedKey)
        : [...current, buildCategoryAssignment(category)];
    });
  };

  const toggleSubcategory = (category, subcategory) => {
    const categoryKey = categoryAssignmentKey(buildCategoryAssignment(category));
    const childKey = subcategoryAssignmentKey(subcategory);
    updateCategoryAssignments((current) =>
      current.map((assignment) => {
        if (categoryAssignmentKey(assignment) !== categoryKey) return assignment;
        const selected = assignmentContainsSubcategory(assignment, subcategory);
        const existingChildren = Array.isArray(assignment.subcategories) ? assignment.subcategories : [];
        const subcategories = selected
          ? existingChildren.filter((child) => subcategoryAssignmentKey(child) !== childKey)
          : [
              ...existingChildren,
              {
                subcategory_id: subcategory.id || null,
                subcategory: subcategory.name || "",
                subcategory_slug: subcategory.slug || null,
              },
            ];
        return {
          ...assignment,
          subcategories,
          subcategory_ids: subcategories.map((child) => child.subcategory_id).filter(Boolean),
        };
      }),
    );
  };

  const makePrimaryCategory = (category) => {
    const wantedKey = categoryAssignmentKey(buildCategoryAssignment(category));
    updateCategoryAssignments((current) => {
      const selected = current.find((assignment) => categoryAssignmentKey(assignment) === wantedKey);
      if (!selected) return current;
      return [selected, ...current.filter((assignment) => categoryAssignmentKey(assignment) !== wantedKey)];
    });
  };

  const removeUnavailableAssignment = (assignment) => {
    const wantedKey = categoryAssignmentKey(assignment);
    updateCategoryAssignments((current) =>
      current.filter((item) => categoryAssignmentKey(item) !== wantedKey),
    );
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const allowedTypes = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);
    const invalidType = files.find((file) => !allowedTypes.has(file.type));
    if (invalidType) {
      toast.error(`${invalidType.name} is not a supported image. Use JPG, PNG, WebP or GIF.`);
      e.target.value = "";
      return;
    }
    const oversized = files.find((file) => file.size > 8 * 1024 * 1024);
    if (oversized) {
      toast.error(`${oversized.name} is larger than 8 MB.`);
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/api/admin/upload", fd);
        uploaded.push(data.url);
      }
      setForm((prev) => {
        const nextImages = [...prev.images, ...uploaded];
        return { ...prev, images: nextImages, main_image: prev.main_image || nextImages[0] };
      });
      toast.success(`${uploaded.length} image(s) uploaded`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((prev) => {
      const images = prev.images.filter((_, i) => i !== idx);
      const main_image = prev.main_image === prev.images[idx] ? images[0] || "" : prev.main_image;
      return { ...prev, images, main_image };
    });
  };

  const setMainImage = (url) => setForm((prev) => ({ ...prev, main_image: url }));

  const addColor = () => {
    const c = colorInput.trim();
    if (!c || form.colors.includes(c)) return;
    setForm({ ...form, colors: [...form.colors, c] });
    setColorInput("");
  };

  const removeColor = (c) => setForm({ ...form, colors: form.colors.filter((x) => x !== c) });

  const applyDescriptionFormat = (type) => {
    const textarea = descriptionRef.current;
    const source = form.description || "";
    const hasSelection = textarea && textarea.selectionStart !== textarea.selectionEnd;
    const start = hasSelection ? textarea.selectionStart : 0;
    const end = hasSelection ? textarea.selectionEnd : source.length;
    const formatted = formatDescriptionLines(source.slice(start, end), type);
    const nextDescription = `${source.slice(0, start)}${formatted}${source.slice(end)}`;

    setForm((previous) => ({ ...previous, description: nextDescription }));
    window.requestAnimationFrame(() => {
      if (!descriptionRef.current) return;
      descriptionRef.current.focus();
      descriptionRef.current.setSelectionRange(start, start + formatted.length);
    });
  };

  const toggleSize = (s) => {
    setForm((prev) => {
      const selected = prev.sizes.includes(s)
        ? prev.sizes.filter((size) => size !== s)
        : [...prev.sizes, s];
      return { ...prev, sizes: STANDARD_PRODUCT_SIZES.filter((size) => selected.includes(size)) };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (form.original_price === "") {
      toast.error("Original price is required");
      return;
    }
    if (form.offer_price !== "" && Number(form.offer_price) > Number(form.original_price)) {
      toast.error("Offer price cannot exceed original price");
      return;
    }
    if (!form.sizes.length) {
      toast.error("Select at least one available size");
      return;
    }
    const placements = normalizeProductCategoryAssignments(form, categories);
    if (!placements.length) {
      toast.error("Select at least one category");
      return;
    }
    const deliveryDays = Number(form.delivery_time_days);
    if (!Number.isInteger(deliveryDays) || deliveryDays < 1 || deliveryDays > 365) {
      toast.error("Delivery time must be a whole number between 1 and 365 days");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      ...derivePrimaryCategoryFields(placements, categories),
      original_price: form.original_price === "" ? null : Number(form.original_price),
      offer_price: form.offer_price === "" ? null : Number(form.offer_price),
      discount_percentage: form.discount_percentage === "" ? null : Number(form.discount_percentage),
      delivery_time_days: deliveryDays,
      slug: form.slug || undefined,
    };
    try {
      if (product?.id) {
        await api.put(`/api/admin/products/${product.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/api/admin/products", payload);
        toast.success("Product created");
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="product-form-overlay">
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">{product ? "Edit product" : "New product"}</span>
            <h2>{product?.name || "Add a look"}</h2>
          </div>
          <button className="icon-btn" onClick={onClose} data-testid="product-form-close" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={save} className="modal-body">
          <div className="form-grid">
            <label className="span-2">
              Product name
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="product-name-input"
              />
            </label>
            <label>
              Look number
              <input
                value={form.look_number || ""}
                onChange={(e) => setForm({ ...form, look_number: e.target.value })}
                placeholder="e.g. Look 20"
                data-testid="product-look-input"
              />
            </label>
            <div className="span-2 product-category-editor" data-testid="product-category-assignments">
              <div className="category-editor-head">
                <div>
                  <span className="eyebrow">Catalog placements</span>
                  <p className="hint">
                    Choose every category where this product should appear. The first selection is the primary category.
                  </p>
                </div>
                <div className="category-editor-actions">
                  <button
                    type="button"
                    onClick={() =>
                      updateCategoryAssignments((current) => {
                        const selectedKeys = new Set(current.map(categoryAssignmentKey));
                        const missing = categories
                          .filter((category) => !selectedKeys.has(categoryAssignmentKey(buildCategoryAssignment(category))))
                          .map((category) => buildCategoryAssignment(category));
                        return [...current, ...missing];
                      })
                    }
                    data-testid="product-categories-select-all"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => updateCategoryAssignments([])}
                    data-testid="product-categories-clear"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="category-assignment-grid">
                {categories.map((category) => {
                  const categoryKey = categoryAssignmentKey(buildCategoryAssignment(category));
                  const assignment = categoryAssignments.find(
                    (item) => categoryAssignmentKey(item) === categoryKey,
                  );
                  const isSelected = Boolean(assignment);
                  const isPrimary = isSelected && categoryAssignmentKey(categoryAssignments[0]) === categoryKey;
                  const selectedChildCount = assignment?.subcategories?.length || 0;

                  return (
                    <section
                      key={category.id || category.slug}
                      className={`category-assignment-card ${isSelected ? "is-selected" : ""}`}
                    >
                      <div className="category-assignment-card-head">
                        <label className="category-assignment-toggle">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleCategory(category)}
                            data-testid={`product-category-${category.slug || category.id}`}
                          />
                          <span>
                            <strong>{category.name}</strong>
                            <small>
                              {isSelected
                                ? selectedChildCount
                                  ? `${selectedChildCount} subcategor${selectedChildCount === 1 ? "y" : "ies"}`
                                  : "Main category only"
                                : "Not selected"}
                            </small>
                          </span>
                        </label>
                        {isPrimary ? (
                          <span className="primary-category-badge">Primary</span>
                        ) : isSelected ? (
                          <button
                            type="button"
                            className="make-primary-button"
                            onClick={() => makePrimaryCategory(category)}
                            data-testid={`make-primary-${category.slug || category.id}`}
                          >
                            Make primary
                          </button>
                        ) : null}
                      </div>

                      {isSelected && (category.children || []).length > 0 && (
                        <div className="subcategory-assignment-panel">
                          <span>Subcategories (optional)</span>
                          <div className="subcategory-assignment-grid">
                            {(category.children || []).map((child) => {
                              const childSelected = assignmentContainsSubcategory(assignment, child);
                              return (
                                <label key={child.id || child.slug}>
                                  <input
                                    type="checkbox"
                                    checked={childSelected}
                                    onChange={() => toggleSubcategory(category, child)}
                                    data-testid={`product-subcategory-${category.slug || category.id}-${child.slug || child.id}`}
                                  />
                                  <span>{child.name}</span>
                                </label>
                              );
                            })}
                          </div>
                          <small className="category-parent-only-note">
                            Leave all unchecked to show this product only on the main {category.name} page.
                          </small>
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>

              {categoryAssignments.some(
                (assignment) =>
                  !categories.some(
                    (category) =>
                      categoryAssignmentKey(buildCategoryAssignment(category)) === categoryAssignmentKey(assignment),
                  ),
              ) && (
                <div className="unavailable-category-list">
                  <span className="eyebrow">Saved placements no longer in the category list</span>
                  {categoryAssignments
                    .filter(
                      (assignment) =>
                        !categories.some(
                          (category) =>
                            categoryAssignmentKey(buildCategoryAssignment(category)) === categoryAssignmentKey(assignment),
                        ),
                    )
                    .map((assignment) => (
                      <div key={categoryAssignmentKey(assignment)}>
                        <span>{assignment.category || assignment.category_slug || "Unknown category"}</span>
                        <button type="button" onClick={() => removeUnavailableAssignment(assignment)}>
                          Remove
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
            <div className="span-2 product-description-editor">
              <div className="product-description-editor-head">
                <label htmlFor="product-description">Description</label>
                <div className="description-format-toolbar" role="toolbar" aria-label="Description formatting">
                  <button type="button" onClick={() => applyDescriptionFormat("bullet")} title="Make a bulleted list">
                    <ListIcon size={14} /> Bullets
                  </button>
                  <button type="button" onClick={() => applyDescriptionFormat("number")} title="Make a numbered list">
                    <ListOrdered size={14} /> Numbers
                  </button>
                  <button type="button" onClick={() => applyDescriptionFormat("plain")} title="Remove list formatting">
                    <AlignLeft size={14} /> Plain
                  </button>
                </div>
              </div>
              <textarea
                id="product-description"
                ref={descriptionRef}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="product-description-input"
                rows={6}
                placeholder={"Write a paragraph, or place each feature on a new line and choose Bullets or Numbers."}
              />
              <small className="field-help">Select specific lines to format only those lines. With no selection, the complete description is formatted.</small>
            </div>
            <label>
              Original price (₹)
              <input
                type="number"
                min="0"
                required
                value={form.original_price}
                onChange={(e) => setForm({ ...form, original_price: e.target.value })}
                data-testid="product-original-price-input"
              />
            </label>
            <label>
              Offer price (₹, optional)
              <input
                type="number"
                min="0"
                value={form.offer_price}
                onChange={(e) => setForm({ ...form, offer_price: e.target.value })}
                data-testid="product-offer-price-input"
              />
            </label>
            <label>
              Discount % (optional)
              <input
                type="number"
                min="0"
                max="100"
                value={form.discount_percentage}
                onChange={(e) => setForm({ ...form, discount_percentage: e.target.value })}
                data-testid="product-discount-input"
              />
            </label>
            <label>
              Delivery time (days)
              <input
                type="number"
                min="1"
                max="365"
                step="1"
                required
                value={form.delivery_time_days}
                onChange={(e) => setForm({ ...form, delivery_time_days: e.target.value })}
                data-testid="product-delivery-time-input"
              />
              <span className="field-help">Shown to customers as the estimated delivery time.</span>
            </label>
            <label>
              Slug (optional)
              <input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="auto-generated"
                data-testid="product-slug-input"
              />
            </label>
          </div>

          <div className="form-block">
            <div className="size-editor-head">
              <div>
                <span className="eyebrow">Available sizes</span>
                <p className="hint">Select every size available for this product. The same house sizes appear on the product page.</p>
              </div>
              <div className="size-editor-actions">
                <button type="button" onClick={() => setForm({ ...form, sizes: [...STANDARD_PRODUCT_SIZES] })}>Select all</button>
                <button type="button" onClick={() => setForm({ ...form, sizes: [] })}>Clear</button>
              </div>
            </div>
            <div className="chip-row size-editor-grid">
              {STANDARD_PRODUCT_SIZES.map((s) => (
                <button
                  type="button"
                  key={s}
                  className={`chip ${form.sizes.includes(s) ? "selected" : ""}`}
                  onClick={() => toggleSize(s)}
                  aria-pressed={form.sizes.includes(s)}
                  data-testid={`size-chip-${sizeTestId(s)}`}
                >
                  {s}
                  {form.sizes.includes(s) && <X size={12} aria-hidden="true" />}
                </button>
              ))}
            </div>
          </div>

          <div className="form-block">
            <span className="eyebrow">Colours</span>
            <div className="chip-row">
              {form.colors.map((c) => (
                <button
                  type="button"
                  key={c}
                  className="chip selected"
                  onClick={() => removeColor(c)}
                  data-testid={`color-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {c} <X size={12} />
                </button>
              ))}
            </div>
            <div className="inline-input">
              <input
                value={colorInput}
                onChange={(e) => setColorInput(e.target.value)}
                placeholder="Add a colour"
                data-testid="color-input"
              />
              <button type="button" onClick={addColor} data-testid="add-color-button">
                Add
              </button>
            </div>
          </div>

          <div className="form-block">
            <span className="eyebrow">Images</span>
            <div className="image-grid">
              {form.images.map((img, i) => (
                <div className={`image-tile ${form.main_image === img ? "main" : ""}`} key={img + i}>
                  <img src={imgUrl(img)} alt={`upload ${i + 1}`} />
                  <div className="image-tile-actions">
                    <button
                      type="button"
                      onClick={() => setMainImage(img)}
                      title="Set as main"
                      data-testid={`set-main-image-${i}`}
                    >
                      <Star size={14} fill={form.main_image === img ? "currentColor" : "none"} />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      title="Remove"
                      data-testid={`remove-image-${i}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              <label className="image-upload">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  multiple
                  onChange={handleUpload}
                  data-testid="image-upload-input"
                />
                <Upload size={18} />
                <span>{uploading ? "Uploading…" : "Upload"}</span>
              </label>
            </div>
            <p className="hint">
              Recommended product image: 1200 × 1600 px portrait (3:4), JPG/PNG/WebP, maximum 8 MB.
              The storefront keeps every image in the same 3:4 frame. The first image is the cover by default — click the star to change.
            </p>
          </div>

          <div className="form-block toggles">
            <label className="toggle">
              <input
                type="checkbox"
                checked={form.is_featured}
                onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                data-testid="product-featured-toggle"
              />
              Featured
            </label>
            <label className="toggle">
              <input
                type="checkbox"
                checked={form.is_new_arrival}
                onChange={(e) => setForm({ ...form, is_new_arrival: e.target.checked })}
                data-testid="product-new-arrival-toggle"
              />
              New arrival
            </label>
            <label className="toggle">
              <input
                type="checkbox"
                checked={form.is_bestseller}
                onChange={(e) => setForm({ ...form, is_bestseller: e.target.checked })}
                data-testid="product-bestseller-toggle"
              />
              Bestseller
            </label>
            <label className="toggle">
              <input
                type="checkbox"
                checked={form.is_top_rated}
                onChange={(e) => setForm({ ...form, is_top_rated: e.target.checked })}
                data-testid="product-top-rated-toggle"
              />
              Top rated
            </label>
            <label className="toggle">
              <input
                type="checkbox"
                checked={form.is_old_deal}
                onChange={(e) => setForm({ ...form, is_old_deal: e.target.checked })}
                data-testid="product-old-deal-toggle"
              />
              Old deal
            </label>
            <label className="toggle">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                data-testid="product-active-toggle"
              />
              Published
            </label>
          </div>

          <div className="modal-foot">
            <button type="button" className="button outline-button" onClick={onClose} data-testid="product-form-cancel">
              Cancel
            </button>
            <button type="submit" className="button dark-button" disabled={saving} data-testid="product-form-save">
              {saving ? "Saving…" : "Save product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
